import { get, post, del } from 'aws-amplify/api';
import {getErrorMessage} from "../stores/StoreUtilities";
import Account from "../model/Account";
import Logger from "../components/Logger";
import Invoice from "../model/stripe/Invoice";
import User from "../model/User";

class BillingAPI {

  currentCustomer: any = undefined

  getProducts = async () => {
    try {
      const restOperation = get({ apiName: "BillingAPI", path: "/products" })
      const { body } = await restOperation.response
      const response = await body.json() as any
      return response
    } catch (err) {
      const message = getErrorMessage(err)
      Logger.error("BillingAPI.getProducts error", message)
      throw new Error(message)
    }
  }

  getPrices = async () => {
    try {
      const restOperation = get({ apiName: "BillingAPI", path: "/prices" })
      const { body } = await restOperation.response
      const response = await body.json() as any
      return response
    } catch (err) {
      const message = getErrorMessage(err)
      Logger.error("BillingAPI.getPrices error", message)
      throw new Error(message)
    }
  }

  getPlans = async () => {
    try {
      const restOperation = get({ apiName: "BillingAPI", path: "/plans" })
      const { body } = await restOperation.response
      const response = await body.json() as any
      return response
    } catch (err) {
      const message = getErrorMessage(err)
      Logger.error("BillingAPI.getPlans error", message)
      throw new Error(message)
    }
  }

  getCoupons = async () => {
    try {
      const restOperation = get({ apiName: "BillingAPI", path: "/coupons" })
      const { body } = await restOperation.response
      const response = await body.json() as any
      return response
    } catch (err) {
      const message = getErrorMessage(err)
      Logger.error("BillingAPI.getCoupons error", message)
      throw new Error(message)
    }
  }

  getCoupon = async (id: string) => {
    try {
      const restOperation = get({ apiName: "BillingAPI", path: `/coupons/${id}` })
      const { body } = await restOperation.response
      const response = await body.json() as any
      return response
    } catch (err) {
      const message = getErrorMessage(err)
      Logger.error("BillingAPI.getCoupon error", message)
      throw new Error(message)
    }
  }

  getCustomer = async (id: string) => {
    try {
      const restOperation = get({ apiName: "BillingAPI", path: `/customers/${id}` })
      const { body } = await restOperation.response
      const response = await body.json() as any
      return response
    } catch (err) {
      const message = getErrorMessage(err)
      Logger.error("BillingAPI.getCustomer error", message)
      throw new Error(message)
    }
  }

  getCurrentCustomer = async (id: string, refresh: boolean = true) => {
    if (!this.currentCustomer || this.currentCustomer['id'] !== id || refresh) {
      this.currentCustomer = await this.getCustomer(id)
    }
    return this.currentCustomer
  }

  createInvoice = async (user: User, account: Account, invoice: Invoice, source?: string) => {
    const init = {
      body: {
        user: {
          id: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          phone: user.phone
        },
        account: {
          id: account.id,
          name: account.name,
          address: account.address,
          city: account.city,
          state: account.state,
          postalCode: account.postalCode
        },
        invoice,
        source
      }
    }
    try {
      const restOperation = post({ apiName: "BillingAPI", path: "/invoices", options: { body: init.body as any } })
      const { body: responseBody } = await restOperation.response
      const response = await responseBody.json() as any
      return response
    } catch (err) {
      const message = getErrorMessage(err)
      Logger.debug("BillingAPI.createInvoice error", message, init.body)
      throw new Error(message)
    }
  }

  deleteSource = async (customerId: string, sourceId: string) => {
    try {
      const restOperation = del({ apiName: "BillingAPI", path: `/sources/${customerId}/${sourceId}` })
      await restOperation.response
      return
    } catch (err) {
      const message = getErrorMessage(err)
      Logger.error("BillingAPI.deleteSource error", message)
      throw new Error(message)
    }

  }

  cancelSubscription = async (subscriptionId: string) => {
    try {
      const restOperation = del({ apiName: "BillingAPI", path: `/subscriptions/${subscriptionId}` })
      await restOperation.response
      return
    } catch (err) {
      const message = getErrorMessage(err)
      Logger.error("BillingAPI.cancelSubscription error", message)
      throw new Error(message)
    }
  }

  getCharges = async (customerId: string) => {
    try {
      const restOperation = get({ apiName: "BillingAPI", path: `/charges/${customerId}` })
      const { body } = await restOperation.response
      const response = await body.json() as any
      return response
    } catch (err) {
      const message = getErrorMessage(err)
      Logger.error("BillingAPI.getCharges error", message)
      throw new Error(message)
    }
  }

  createCheckoutSession = async (price: string, return_url: string, customer_email?: string, client_reference_id?: string)=> {
    const init = {
      body: {
        price: price,
        return_url: return_url,
        customer_email: customer_email,
        client_reference_id: client_reference_id
      }
    }

    try {
      const restOperation = post({ apiName: "BillingAPI", path: "/create-checkout-session", options: { body: init.body as any } })
      const { body: responseBody } = await restOperation.response
      const response = await responseBody.json() as any
      return response
    } catch (err) {
      const message = getErrorMessage(err)
      Logger.debug("BillingAPI.createCheckoutSession error", message, init.body)
      throw new Error(message)
    }
  }

  getSession = async (sessionId: string) => {
    try {
      const restOperation = get({ apiName: "BillingAPI", path: `/session`, options: { queryParams: { session_id: sessionId } } })
      const { body } = await restOperation.response
      const response = await body.json() as any
      return response
    } catch (err) {
      const message = getErrorMessage(err)
      Logger.error("BillingAPI.getSession error", message)
      throw new Error(message)
    }
  }

  createCustomerPortalSession = async (customer: string, return_url: string) => {
    const init = {
      body: {
        customer: customer,
        return_url: return_url
      }
    }

    try {
      const restOperation = post({ apiName: "BillingAPI", path: "/create-customer-portal-session", options: { body: init.body as any } })
      const { body: responseBody } = await restOperation.response
      const response = await responseBody.json() as any
      return response
    } catch (err) {
      const message = getErrorMessage(err)
      Logger.debug("BillingAPI.createCustomerPortalSession error", message, init.body)
      throw new Error(message)
    }
  }

}

export default BillingAPI